import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, X, Loader2, MapPin, Radar, Users2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MapPicker from "@/components/MapPicker";
import { toast } from "sonner";

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <h3 className="font-head text-lg font-bold text-slate-900">{title}</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div>{children}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const empty = { name: "", latitude: "", longitude: "", radius: 150, address: "", manager_emails: "" };

export default function Sites() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canEdit = me?.can_edit;
  const canDelete = me?.can_delete;
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");

  const { data: sites = [], isLoading } = useQuery({ queryKey: ["sites"], queryFn: () => api.get("/admin/sites") });
  const isSuper = me?.role === "super_admin";
  const [assignSite, setAssignSite] = useState(null);
  const { data: admins = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get("/admin/users"),
    enabled: isSuper,
    select: (users) => users.filter((u) => u.role === "admin"),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["sites"] });

  const saveMut = useMutation({
    mutationFn: (body) => (editing ? api.put(`/admin/sites/${editing.id}`, body) : api.post("/admin/sites", body)),
    onSuccess: () => { toast.success(editing ? "Site updated" : "Site created"); setModal(false); invalidate(); },
    onError: (e) => setErr(apiError(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/sites/${id}`),
    onSuccess: () => { toast.success("Site deleted"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const openAdd = () => { setEditing(null); setForm(empty); setErr(""); setModal(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, latitude: s.latitude, longitude: s.longitude, radius: s.radius, address: s.address || "", manager_emails: (s.manager_emails || []).join(", ") }); setErr(""); setModal(true); };

  const submit = (e) => {
    e.preventDefault();
    setErr("");
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (!isFinite(lat) || !isFinite(lng)) {
      setErr("Enter a valid latitude and longitude, or pick a location on the map.");
      return;
    }
    if (lat < -90 || lat > 90) { setErr("Latitude must be between -90 and 90."); return; }
    if (lng < -180 || lng > 180) { setErr("Longitude must be between -180 and 180."); return; }
    saveMut.mutate({
      name: form.name,
      latitude: lat,
      longitude: lng,
      radius: parseInt(form.radius, 10) || 150,
      address: form.address || null,
      manager_emails: form.manager_emails || "",
    });
  };

  // Allow pasting a "lat, lng" pair into the latitude box — splits into both fields.
  const handleLatChange = (v) => {
    if (typeof v === "string" && v.includes(",")) {
      const [a, b] = v.split(",").map((x) => x.trim());
      setForm((f) => ({ ...f, latitude: a, ...(b !== undefined && b !== "" ? { longitude: b } : {}) }));
    } else {
      setForm((f) => ({ ...f, latitude: v }));
    }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="sites-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{sites.length} geofenced site(s)</p>
        <button onClick={openAdd} data-testid="site-create-btn" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition shadow-sm shadow-indigo-600/20">
          <Plus className="h-4 w-4" /> Add Site
        </button>
      </div>

      {isLoading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="sites-grid">
          {sites.map((s) => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:border-slate-300 hover:shadow-md transition-all" data-testid={`site-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="grid place-items-center h-11 w-11 rounded-xl bg-sky-50 text-sky-600"><MapPin className="h-5 w-5" /></div>
                <div className="flex gap-1">
                  {isSuper && (
                    <button onClick={() => setAssignSite(s)} data-testid={`site-assign-${s.id}`} title="Assign admins" className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition"><Users2 className="h-4 w-4" /></button>
                  )}
                  {canEdit && (
                    <button onClick={() => openEdit(s)} data-testid={`site-edit-${s.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 transition"><Pencil className="h-4 w-4" /></button>
                  )}
                  {canDelete && (
                    <button onClick={() => { if (window.confirm(`Delete ${s.name}?`)) delMut.mutate(s.id); }} data-testid={`site-delete-${s.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
              <h3 className="mt-4 font-head text-lg font-bold text-slate-900">{s.name}</h3>
              {s.address && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{s.address}</p>}
              <div className="mt-4 flex items-center gap-2 text-xs">
                <span className="font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded">{Number(s.latitude).toFixed(4)}, {Number(s.longitude).toFixed(4)}</span>
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-sm text-slate-600">
                <Radar className="h-4 w-4 text-indigo-500" />
                <span className="font-semibold text-slate-800">{s.radius}m</span> geofence radius
              </div>
              {isSuper && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500" data-testid={`site-assignees-${s.id}`}>
                  <span className="font-semibold text-slate-600">Access:</span>{" "}
                  {(s.assigned_admins && s.assigned_admins.length)
                    ? admins.filter((a) => s.assigned_admins.includes(a.id)).map((a) => a.name).join(", ") || `${s.assigned_admins.length} admin(s)`
                    : "Owner only"}
                </div>
              )}
            </div>
          ))}
          {sites.length === 0 && <div className="col-span-full py-16 text-center text-slate-400">No sites yet. Add your first site.</div>}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Site" : "Add Site"}>
        <form onSubmit={submit} className="grid lg:grid-cols-2" data-testid="site-form">
          {/* Map column */}
          <div className="p-5 lg:border-r border-slate-100">
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">Pick location on map</label>
            <MapPicker
              height="h-[360px]"
              value={{ latitude: form.latitude, longitude: form.longitude }}
              onChange={({ latitude, longitude, address }) =>
                setForm((f) => ({ ...f, latitude, longitude, address: address ?? f.address }))
              }
            />
          </div>

          {/* Form column */}
          <div className="p-5 space-y-4 flex flex-col">
            <Field label="Site Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="site-name" />
            <Field label="Formatted Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="site-address" placeholder="Auto-filled from map — editable" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Latitude" value={form.latitude} onChange={handleLatChange} testid="site-lat" placeholder="e.g. 37.3349" inputMode="decimal" />
              <Field label="Longitude" value={form.longitude} onChange={(v) => setForm({ ...form, longitude: v })} testid="site-lng" placeholder="e.g. -122.0090" inputMode="decimal" />
            </div>
            <p className="-mt-2 text-xs text-slate-400">Type or paste coordinates (e.g. <span className="font-mono">37.3349, -122.0090</span>) — the map pin updates instantly.</p>
            <Field label="Geofence Radius (meters)" value={form.radius} onChange={(v) => setForm({ ...form, radius: v })} type="number" testid="site-radius" />
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Site Manager Emails</label>
              <textarea
                data-testid="site-manager-emails"
                value={form.manager_emails}
                onChange={(e) => setForm({ ...form, manager_emails: e.target.value })}
                placeholder="manager1@company.com, manager2@company.com"
                rows={2}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 resize-none"
              />
              <p className="mt-1.5 text-xs text-slate-400">Separate multiple emails with commas. Managers get an email — with a live site attendance summary — every time a staff member here clocks in (on time or late).</p>
            </div>
            {err && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="site-form-error">{err}</p>}
            <div className="flex justify-end gap-2 pt-2 mt-auto">
              <button type="button" onClick={() => setModal(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saveMut.isPending} data-testid="site-save-btn" className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
                {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save" : "Create Site"}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <AssignModal site={assignSite} admins={admins} onClose={() => setAssignSite(null)} onDone={invalidate} />
    </div>
  );
}

function AssignModal({ site, admins, onClose, onDone }) {
  const [selected, setSelected] = useState({ __init: null, ids: [] });
  const mut = useMutation({
    mutationFn: (ids) => api.put(`/admin/sites/${site.id}/assignees`, { admin_ids: ids }),
    onSuccess: () => { toast.success("Site access updated"); onDone(); onClose(); },
    onError: (e) => toast.error(apiError(e)),
  });
  if (!site) return null;
  const open = true;
  const current = site?.assigned_admins || [];
  const list = selected.__init === site?.id ? (selected.ids || []) : current;
  const toggle = (id) => {
    const base = selected.__init === site?.id ? selected.ids : current;
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    setSelected({ __init: site.id, ids: next });
  };
  return (
    <Modal open={open} onClose={onClose} title={`Assign Admins — ${site?.name || ""}`}>
      <div className="p-6 max-w-md" data-testid="assign-modal">
        <p className="text-sm text-slate-500 mb-4">Select which admins can see and manage this site. The owning admin always has access.</p>
        {admins.length === 0 ? (
          <p className="text-sm text-slate-400">No admin users exist yet.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {admins.map((a) => {
              const on = list.includes(a.id);
              const isOwner = a.id === site?.created_by;
              return (
                <button key={a.id} type="button" disabled={isOwner} data-testid={`assign-admin-${a.id}`} onClick={() => toggle(a.id)}
                  className={`flex items-center justify-between gap-3 w-full h-12 px-3 rounded-lg border transition ${isOwner ? "bg-slate-50 border-slate-200 opacity-70" : on ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200 hover:border-indigo-300"}`}>
                  <span className="text-left">
                    <span className="block text-sm font-semibold text-slate-700">{a.name}</span>
                    <span className="block text-[11px] text-slate-400">{a.email}{isOwner ? " · Owner" : ""}</span>
                  </span>
                  <span className={`h-5 w-9 rounded-full relative transition ${on || isOwner ? "bg-emerald-500" : "bg-slate-300"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on || isOwner ? "left-4" : "left-0.5"}`} />
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-5">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="button" disabled={mut.isPending} data-testid="assign-save-btn" onClick={() => mut.mutate(list)}
            className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save Access
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder, readOnly, inputMode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">{label}</label>
      <input data-testid={testid} type={type} inputMode={inputMode} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} readOnly={readOnly}
        className={`w-full h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${readOnly ? "bg-slate-50 text-slate-500 font-mono" : "bg-white"}`} />
    </div>
  );
}
